import { useId, useState } from 'react';
import { type Domain, DOMAIN_LABELS } from '@zevik/schemas';
import { Refused } from './credential/CredentialShell';

/**
 * The first screen a client sees of their own assessment: confirming which
 * domains apply to the system (docs/DECISIONS.md ZD-121 §6, ZD-124 §2).
 *
 * Every enabled domain is pre-selected. A client confirming *which of IT,
 * privacy and AI applies to this system* is confirming their own scope,
 * which they can do; an empty choice would be asking them to know what a
 * pack is, which they cannot. The list is the assessment's
 * `enabled_domains` — frozen from the tenant's set when it was created — and
 * nothing here knows what a domain is beyond the words `DOMAIN_LABELS` gives
 * it.
 *
 * Confirming is `/domain-selection` and then `/advance`, in that order, as
 * two acts the page makes because the API keeps them apart (ZD-121 §5.1:
 * one derivation of what starts a run). The guard's refusals — none chosen,
 * one the tenant is not enabled for — arrive as the API's sentence, and the
 * confirm button is not disabled at zero: that would be the guard's `≥1`
 * restated in a bundle, and the sentence is the API's to say.
 */
export function DomainConfirmation({
  enabled,
  onConfirm,
}: {
  enabled: Domain[];
  onConfirm: (domains: Domain[]) => Promise<void>;
}): React.JSX.Element {
  const groupId = useId();
  const [chosen, setChosen] = useState<Set<Domain>>(() => new Set(enabled));
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setRefusal(null);
    setSending(true);
    try {
      await onConfirm(enabled.filter((domain) => chosen.has(domain)));
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className="domains"
      aria-labelledby={groupId}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2 id={groupId}>Confirm what applies to this system</h2>
      <p className="domains__lead">
        The documents have been read and the system modelled. Before it is analysed, confirm which
        of these apply. All of them are selected; untick one only if it plainly does not apply.
      </p>
      <ul className="domains__list">
        {enabled.map((domain) => {
          const id = `${groupId}-${domain}`;
          return (
            <li key={domain}>
              <input
                id={id}
                type="checkbox"
                checked={chosen.has(domain)}
                onChange={(event) => {
                  setChosen((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(domain);
                    else next.delete(domain);
                    return next;
                  });
                }}
              />
              <label htmlFor={id}>
                <span className="domains__name">{DOMAIN_LABELS[domain].label}</span>
                <span className="domains__about">{DOMAIN_LABELS[domain].about}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {refusal === null ? null : <Refused message={refusal} />}
      <div className="domains__actions">
        <button type="submit" className="submit" disabled={sending}>
          {sending ? 'Confirming…' : 'Confirm and continue'}
        </button>
      </div>
    </form>
  );
}
